import { CommonModule, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { DateAdapter, MAT_DATE_LOCALE, MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { NgxMatDatepickerInput, NgxMatDatetimepicker } from '@ngxmc/datetime-picker';
import { Observable, Subject, switchMap, tap } from 'rxjs';
import { UpdateAttributeActionData } from 'src/app/components/designer/models/update-attribute-action-data';
import { Attribute } from 'src/app/models/attribute';
import { Customer } from 'src/app/models/customer';
import { PropertyType } from 'src/app/models/enums/property-type';
import { FolderState } from 'src/app/models/folder-state';
import { RestApiConfiguration } from 'src/app/models/RestApiCaller/configuration/rest-api-configuration';
import { AutocompletionService } from 'src/app/services/autocompletion.service';
import { RestApiConfigurationsService } from 'src/app/services/restApi/rest-api-configurations.service';
import { FormMode } from '../../common/form-mode';
import { ContextualMenuEvent } from '../contextual-menu-event';
import { NeedAttributes } from '../need-attributes';
import { NeedFolderStates } from '../need-folder-states';
import { WorkflowDataInterface } from '../workflow-data.interface';
import { User } from 'src/app/models/user';
import { NeedUsers } from '../need-users';

@Component({
    selector: 'app-workflow-action-update-attribute-data',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        NgClass,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        FaIconComponent,
        MatDatepickerModule,
        CommonModule,
        MatTooltipModule,
        MatAutocompleteModule,
        NgxMatDatepickerInput,
        NgxMatDatetimepicker,
    ],
    templateUrl: './workflow-action-update-attribute-data.component.html',
    styleUrl: './workflow-action-update-attribute-data.component.scss',
    providers: [
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
    ],
})
export class WorkflowActionUpdateAttributeDataComponent implements WorkflowDataInterface, OnInit, NeedAttributes, NeedFolderStates, NeedUsers {
    /**
     * Liste des icones utilisés dans le template.
     */
    public faCircleInfo = faCircleInfo;

    /**
     * Liste des Enums utilisées dans le template.
     */

    public propertyType: typeof PropertyType = PropertyType;
    public FormMode = FormMode;

    /**
     * Données affichées par le composant dynamique.
     */
    public _data: UpdateAttributeActionData;

    /**
     * Spécifie les données affichées par le composant dynamique.
     */
    set data(value: string | UpdateAttributeActionData) {
        if (typeof value === 'string') {
            this._data = JSON.parse(value);
        } else {
            this._data = value as UpdateAttributeActionData;
        }

        this.fillForm();
    }

    /**
     * Obtient les données affichées par le composant dynamique.
     */
    get data(): UpdateAttributeActionData {
        this.fillData();
        return this._data;
    }

    /**
     * Mode d'affichage du formulaire.
     */
    public mode: FormMode;

    /**
     * Retourne une valeur indiquant si l'utilisateur a modifié des valeurs du formulaire.
     */
    public get dirty(): boolean {
        return this.dataForm && this.dataForm.dirty;
    }

    /**
     * Source du menu contextuel.
     */
    contextualMenuSource: Subject<ContextualMenuEvent> = new Subject<ContextualMenuEvent>();

    /**
     * Liste des attributs de la vue.
     */
    public _attributes: Attribute[];

    /**
     * Spécifie la liste des attributs de la vue.
     */
    set attributes(value: Attribute[]) {
        // Ne propose pas les attributs correspondant à l'id du dossier et à sa date de création (qui ne sont pas modifiables)
        this._attributes = value.filter((x) => x.id !== 8 && x.id !== 2);
        this.fillForm();
    }

    /**
     * Liste des états de dossier.
     */
    public _folderStates: FolderState[];

    /**
     * Spécifie la Liste des états de dossier.
     */
    set folderStates(value: FolderState[]) {
        this._folderStates = value;
    }

    /**
     * Liste des clients.
     */
    public _customers: Customer[];

    /**
     * Spécifie la Liste des clients.
     */
    set customers(value: Customer[]) {
        this._customers = value;
    }

    /**
     * Liste des utilisateurs.
     */
    public _users: User[];

    /**
     * Spécifie la Liste des utilisateurs.
     */
    set users(value: User[]) {
        this._users = value;
    }

    /**
     * Instance du formulaire.
     */
    public dataForm: UntypedFormGroup;

    /**
     * Attribut selectionné par l'utilisateur
     */
    public selectedAttribute: Attribute = null;

    /**
     * Liste des valeurs proposées par l'autocomplétion.
     * (Utilisé uniquement par le type d'attribut 'Autocompletion par API')
     */
    public autocompletionValues: Observable<string[]>;

    /**
     * Instance de la configuration d'API.
     * (Utilisé uniquement par le type d'attribut 'Autocompletion par API')
     */
    public restApiConfiguration: RestApiConfiguration = null;

    /**
     * Valeur selectionnée dans le cas d'un attribut de type 'Autocompletion par API'.
     */
    private autoCompletionSelectedValue: string = '';

    /**
     * Valeur indiquant que l'attribut n'est pas renseigné alors qu'il est obligatoire.
     */
    public isRequiredError: boolean = false;

    /**
     * Constructeur.
     */
    constructor(
        private changeDetector: ChangeDetectorRef,
        private autocompletionService: AutocompletionService,
        private restApiConfigurationsService: RestApiConfigurationsService
    ) {}

    /**
     * Appellé lors de l'initialisation du contrôle.
     */
    ngOnInit(): void {
        this.dataForm = new UntypedFormGroup({
            attribute: new UntypedFormControl('', [Validators.required]),
            value: new UntypedFormControl('', [Validators.required]),
        });

        if (this.mode === FormMode.Reading) {
            this.dataForm.disable();
        } else {
            this.dataForm.enable();
        }

        this.fillForm();
    }

    /**
     * Indique si le formulaire est valide.
     */
    public isValid(): boolean {
        return this.dataForm.valid;
    }

    /**
     * Annulle les changements effectués par l'utilisateur.
     */
    public clearData() {
        this.dataForm.markAsPristine();
    }

    /**
     * Appellée lorsque l'utilisateur selectionne un attribut.
     */
    public onSelectedAttributeChange(event: MatSelectChange) {
        this.displayFormForAttribute(event.value, '');
    }

    /**
     * Appelée lorsqu'une valeur de la liste proposée par l'autocomplétion est selectionnée.
     * @param event Événement.
     */
    public onAutocompleteItemSelected(event: MatAutocompleteSelectedEvent): void {
        let value: string = event.option.value;

        this.isRequiredError = false;

        this.autoCompletionSelectedValue = value;
        this.dataForm.get('value').setValue(value);
    }

    /**
     * Appelée lorsque la liste proposée par l'autocomplétion est fermée.
     */
    public onAutocompleteClosed(): void {
        console.log('closed');
        this.isRequiredError = false;

        let value = this.dataForm.get('value').value;

        if (this.autoCompletionSelectedValue !== value && value !== '') {
            this.dataForm.get('value').setValue(this.autoCompletionSelectedValue);
        }

        if (!!!value || value === '') {
            this.isRequiredError = true;
        }
    }

    /**
     * Levé lorsque l'utilisateur fait un clic-droit sur un composant Input.
     * @param source : Identifiant du composant. Permet de déterminer dans quel composant le paramètre sera ajouté.
     * @param event : Événement souris.
     */
    public onInputRightClick(source: string, event: MouseEvent) {
        this.contextualMenuSource.next(new ContextualMenuEvent(this.dataForm.get(source), event));
    }

    /**
     * Remplit le formulaire avec les données.
     */
    private fillForm() {
        if (this.dataForm) {
            if (this._data && this._attributes) {
                this.dataForm.get('attribute').setValue(this._data.attributeId);

                this.displayFormForAttribute(this._data.attributeId, this._data.value);
            }
        }
    }

    /**
     * Met à jour les données à partir du formulaire.
     */
    private fillData() {
        let updatedData: UpdateAttributeActionData = new UpdateAttributeActionData();
        updatedData.attributeId = this.dataForm.get('attribute').value;
        updatedData.value = this.dataForm.get('value').value;

        this._data = updatedData;
    }

    /**
     * Affiche le formulaire adapté pour l'attribut spécifié
     * @param attributeId : Ide de l'attribut.
     * @param defaultValue : Valeur par défaut.
     */
    private displayFormForAttribute(attributeId: number, defaultValue: any) {
        if (attributeId) {
            let attribute = this._attributes.find((x) => x.id === attributeId);

            this.dataForm.setControl('value', new UntypedFormControl(defaultValue, [Validators.required]));

            if (attribute.type === PropertyType.Long) {
                this.dataForm.controls['value'].addValidators(IsInteger());
            }

            if (attribute.type === PropertyType.Decimal) {
                this.dataForm.controls['value'].addValidators(IsDecimal());
            }

            if (!!attribute.format) {
                this.dataForm.controls['value'].addValidators(Validators.pattern(attribute.format));
            }

            if (this.mode === FormMode.Reading) {
                this.dataForm.controls['value'].disable();
            } else {
                this.dataForm.controls['value'].enable();
            }

            this.dataForm.updateValueAndValidity();

            this.selectedAttribute = attribute;
            if (attribute.type === PropertyType.AutocompletionByApi) {
                // this.filteredOptions = this.attributeFormControl.valueChanges.pipe(
                //     startWith(''),

                //     debounceTime(400),

                //     distinctUntilChanged(),

                //     switchMap((val) => {
                //         return this.filter(val || '');
                //     })
                // );
                // Chargement de la configuration d'API
                this.initAutoCompletion(attribute);
            }
        }
    }

    /**
     * Initialise l'autocompletion pour un attribut.
     * @param attribute : Instance de l'attribut.
     */
    private initAutoCompletion(attribute: Attribute) {
        this.restApiConfigurationsService.get(attribute.configurationApiId).subscribe((x) => {
            this.restApiConfiguration = x;
        });

        this.autocompletionValues = this.dataForm.controls['value'].valueChanges.pipe(
            tap((value) => console.log('Autocomplete : ', value)),
            switchMap((val) => {
                return this.getAutocompletionList(val || '');
            })
        );
    }

    /**
     * Retourne la liste des données correspondant à la saisie de l'utilisateur.
     * @param userInput : saisie de l'utilisateur
     * @returns : Une liste de données.
     */
    private getAutocompletionList(userInput: string): Observable<string[]> {
        return this.autocompletionService.getDatas(this.restApiConfiguration, userInput.toLowerCase());
    }
}

/**
 * Fonction permettant de valider que le la valeur d'un champ est un nombre entier.
 * @returns Une erreur si la valeur du champ n'est pas un nombre entier; null autrement.
 */
export function IsInteger(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const numberValue = Number(control.value);

        if (Number.isInteger(numberValue) === false) {
            return { invalidError: true };
        } else {
            return null;
        }
    };
}

/**
 * Fonction permettant de valider que le la valeur d'un champ est un nombre décimal.
 * @returns Une erreur si la valeur du champ n'est pas un nombre décimal; null autrement.
 */
export function IsDecimal(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const decimalPattern: RegExp = /^-?\d+(,\d+)*(\.\d+(e\d+)?)?$/;

        if (typeof control.value === 'number') {
            return null;
        }

        let inputText = control.value as string;

        if (inputText === null || inputText === '') {
            // Saisie vide
            return null;
        } else {
            // Formate la valeur saisie si le séparateur décimal virgule est utilisé au lieu du spéparateur point.
            inputText = inputText.replace(',', '.');

            const numberValue = Number.parseFloat(inputText);

            // Test si la valeur est un décimal (au sens javascript --> permet de saisir des caractères texte avant et aprés) et si elle respecte la regex decimale.
            if (isNaN(numberValue) || !decimalPattern.test(inputText)) {
                return { invalidError: true };
            } else {
                return null;
            }
        }
    };
}
